import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllDriverDeposits,
  deleteDriverDeposit,
} from "../../api/driverDepositApi";
import { getAllDrivers } from "../../api/driverApi";
import { getDepositExpenses } from "../../api/driverExpenseApi";
import { toast } from "react-toastify";
import SafeImage from "../../components/common/SafeImage";
import { getCurrencySymbolFromPhone } from "../../utils/currencyFromPhone";
import CloseIcon from "@mui/icons-material/Close";

// Debounce hook
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// Helper: Calculate running balances for driver expenses
function getDriverExpenseHistoryWithBalances(expenses, initialBalance) {
  // Sort by date ascending (oldest first)
  const sortedExpenses = [...expenses].sort(
    (a, b) => new Date(a.expense_date) - new Date(b.expense_date),
  );
  let balance = parseFloat(initialBalance);
  // For each expense, subtract and record the balance after
  const history = sortedExpenses.map((exp) => {
    balance -= parseFloat(exp.amount);
    return { ...exp, balance: balance };
  });
  return {
    expensesWithBalance: history,
    currentBalance: balance,
  };
}

/**
 * TransactionDetails Component
 * Shows deposit expense history in chronological order with fixed balance per row (from backend)
 */
const TransactionDetails = React.memo(function TransactionDetails({
  name,
  balance,
  depositId,
  onClose,
  currencySymbol = " ",
}) {
  const navigate = useNavigate(); //  declare it here
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [error, setError] = useState(null);
  const [editExpenseModal, setEditExpenseModal] = useState(null);
  const [editExpenseData, setEditExpenseData] = useState({
    amount: "",
    description: "",
    expense_date: "",
  });

  // Fetch deposit expense history from API
  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        setLoading(true);
        const data = await getDepositExpenses(depositId);
        setExpenses(data);
        setError(null);
      } catch {
        setError("Failed to load expense history");
        toast.error("Failed to load expense history");
      } finally {
        setLoading(false);
      }
    };
    if (depositId) fetchExpenses();
  }, [depositId]);

  // Sort expenses newest first for display
  const expensesSorted = useMemo(
    () =>
      [...expenses].sort(
        (a, b) => new Date(b.expense_date) - new Date(a.expense_date),
      ),
    [expenses],
  );

  // Fallback: calculate running balance if missing in any expense (must process in ascending order for correct balance)
  const expensesWithBalance = useMemo(() => {
    // For balance calculation, always process oldest first
    const asc = [...expensesSorted].sort(
      (a, b) => new Date(a.expense_date) - new Date(b.expense_date),
    );
    let running = parseFloat(balance);
    const withBalance = asc.map((exp) => {
      let expBalance = exp.balance;
      if (typeof expBalance === "undefined" || expBalance === null) {
        running -= parseFloat(exp.amount);
        expBalance = running;
      }
      return { ...exp, balance: expBalance };
    });
    // But display newest first
    return withBalance.reverse();
  }, [expensesSorted, balance]);

  // The current balance is the balance of the last expense, or the initial balance if no expenses
  const currentBalance = useMemo(() => {
    if (expensesWithBalance.length > 0) {
      const last = expensesWithBalance[expensesWithBalance.length - 1].balance;
      return typeof last === "number" ? last : parseFloat(last) || 0;
    }
    return parseFloat(balance) || 0;
  }, [expensesWithBalance, balance]);

  return (
    <div
      className="p-3 border-top details-slider"
      style={{ transition: "all 0.3s cubic-bezier(.4,0,.2,1)" }}
    >
      <div className="d-flex justify-content-between">
        <div>
          <strong>Driver:</strong> {name}
        </div>
        <div>
          <strong>Current Balance:</strong>{" "}
          {typeof currentBalance === "number" && !isNaN(currentBalance)
            ? (parseFloat(currentBalance) === 0
              ? currentBalance.toFixed(2)
              : `${currencySymbol} ${currentBalance.toFixed(2)}`)

            : "-"}
        </div>
      </div>
      <hr />
      <h6>Expense History</h6>
      <div style={{ maxWidth: 600 }}>
        {loading ? (
          <div className="text-center py-3">
            <div
              className="skeleton-loader"
              style={{
                height: 32,
                borderRadius: 6,
                background:
                  "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
                backgroundSize: "200% 100%",
                animation: "skeleton 1.2s infinite linear",
              }}
            />
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Expense Amount</th>
                <th>Balance</th>
                <th>Actions</th> {/* Added Actions column */}
              </tr>
            </thead>
            <tbody>
              {expensesWithBalance.length > 0 ? (
                expensesWithBalance.map((expense) => (
                  <tr key={expense.expense_id || Math.random()}>
                    <td>
                      {expense.expense_date
                        ? new Date(expense.expense_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>{expense.description || "-"}</td>
                    <td className="text-danger fw-bold">
                      {expense.amount
                        ? (parseFloat(expense.amount) === 0
                          ? parseFloat(expense.amount).toFixed(2)
                          : `-${currencySymbol} ${parseFloat(expense.amount).toFixed(2)}`)

                        : "-"}
                    </td>
                    <td className="fw-semibold">
                      {typeof expense.balance !== "undefined" &&
                        expense.balance !== null
                        ? (parseFloat(expense.balance) === 0
                          ? parseFloat(expense.balance).toFixed(2)
                          : `${currencySymbol} ${parseFloat(expense.balance).toFixed(2)}`)

                        : "-"}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            const queryParams = new URLSearchParams({
                              driver_id: expense.driver_id,
                              deposit_id: expense.deposit_id,
                              expense_id: expense.expense_id,
                              amount: expense.amount,
                              description: expense.description
                                ? encodeURIComponent(expense.description)
                                : "",
                              expense_date: expense.expense_date || "",
                            }).toString();

                            navigate(
                              `/accounts/add-driver-expense?${queryParams}`,
                            );
                          }}
                        >
                          Edit
                        </button>

                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            // Delete expense from local state (and backend if needed)
                            if (
                              window.confirm(
                                "Are you sure you want to delete this expense?",
                              )
                            ) {
                              const updatedExpenses = expenses.filter(
                                (e) => e.expense_id !== expense.expense_id,
                              );
                              setExpenses(updatedExpenses);

                              toast.success("Expense deleted successfully");
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No expenses found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="d-flex justify-content-end mt-2">
        <button className="btn btn-link" onClick={onClose}>
          Close
        </button>
      </div>
      {editExpenseModal && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block", zIndex: 2200 }}
            tabIndex="-1"
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Expense</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setEditExpenseModal(null)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editExpenseData.amount}
                      onChange={(e) =>
                        setEditExpenseData({
                          ...editExpenseData,
                          amount: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editExpenseData.description}
                      onChange={(e) =>
                        setEditExpenseData({
                          ...editExpenseData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Expense Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editExpenseData.expense_date?.split("T")[0] || ""}
                      onChange={(e) =>
                        setEditExpenseData({
                          ...editExpenseData,
                          expense_date: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEditExpenseModal(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      // Update expense in state
                      const updatedExpenses = expenses.map((exp) =>
                        exp.expense_id === editExpenseModal.expense_id
                          ? { ...exp, ...editExpenseData }
                          : exp,
                      );
                      setExpenses(updatedExpenses);
                      setEditExpenseModal(null);

                      toast.success("Expense updated successfully");

                      // Optional: call backend API to update
                      // await updateExpense(editExpenseModal.expense_id, editExpenseData);
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 2150 }}
          ></div>
        </>
      )}

      <style>{`
        @keyframes skeleton {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .details-slider {
          animation: slideDown 0.3s cubic-bezier(.4,0,.2,1);
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
});

/**
 * DriverDepositCash Component
 * Main dashboard for managing driver petty cash deposits and viewing expense history
 * Provides CRUD operations for driver deposits with filtering and detailed views
 */
const DriverDepositCash = () => {
  const navigate = useNavigate();
  const [deposits, setDeposits] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [depositToDeleteId, setDepositToDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [openDetailsId, setOpenDetailsId] = useState(null);

  // Debounced driver filter
  const debouncedDriver = useDebouncedValue(selectedDriver, 200);
  const driverCurrencyByName = useMemo(() => {
    const map = new Map();
    drivers.forEach((d) => {
      const name = d?.user?.name;
      const phone = d?.user?.phone_no;
      if (name) map.set(name, getCurrencySymbolFromPhone(phone));
    });
    return map;
  }, [drivers]);
  const selectedDepositSym = useMemo(() => {
    if (!selectedDeposit) return " ";

    // selectedDeposit.driver_name → find driver → phone → currency symbol
    const driver = drivers.find((d) => d?.user?.name === selectedDeposit.driver_name);

    const phone = driver?.user?.phone_no;
    return getCurrencySymbolFromPhone(phone) || " ";
  }, [selectedDeposit, drivers]);


  // Load driver deposits and driver list from API
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [depositData, driverData] = await Promise.all([
        getAllDriverDeposits(),
        getAllDrivers(),
      ]);

      setDeposits(depositData);
      setDrivers(driverData);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle delete confirmation modal display
  const handleDeleteClick = (id) => {
    setDepositToDeleteId(id);
    setShowDeleteModal(true);
  };

  // Execute deposit deletion and update local state
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await deleteDriverDeposit(depositToDeleteId);
      setDeposits(deposits.filter((d) => d.deposit_id !== depositToDeleteId));
      toast.success("Deposit deleted successfully");
    } catch {
      toast.error("Failed to delete deposit");
    } finally {
      setShowDeleteModal(false);
      setDepositToDeleteId(null);
      setDeleteLoading(false);
    }
  };

  // Memoize filtered deposits
  const filteredDeposits = useMemo(
    () =>
      selectedDriver
        ? deposits.filter((d) => d.driver_name === selectedDriver)
        : deposits,
    [deposits, selectedDriver],
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h2 className="fw-bold">Driver Deposit Cash</h2>
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/accounts/add-driver-deposit-cash")}
          >
            Add Driver Deposit
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/accounts/add-driver-expense")}
          >
            Add Driver Expense
          </button>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-md-4">
          <label htmlFor="driverFilter" className="form-label">
            Filter by Driver:
          </label>
          <select
            id="driverFilter"
            className="form-select"
            value={selectedDriver}
            onChange={(e) => setSelectedDriver(e.target.value)}
            disabled={loading}
            aria-label="Filter by driver"
          >
            <option value="">All Drivers</option>
            {drivers.map((driver) => (
              <option key={driver.deposit_id} value={driver.deposit_id}>
                {driver.user.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-striped theme-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Driver Name</th>
              <th>Date</th>
              <th>Petty Cash</th>
              <th>Expense</th>
              <th>Balance</th>
              <th>Preview</th>
              <th>Expense</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>
                  <div
                    className="skeleton-loader"
                    style={{
                      height: 40,
                      borderRadius: 8,
                      background:
                        "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
                      backgroundSize: "200% 100%",
                      animation: "skeleton 1.2s infinite linear",
                    }}
                  />
                </td>
              </tr>
            ) : filteredDeposits.length > 0 ? (
              filteredDeposits.map((deposit) => {
                const sym = driverCurrencyByName.get(deposit.driver_name) || " ";

                // Calculate total expense and balance for each deposit
                const totalExpense = Array.isArray(deposit.expenses)
                  ? deposit.expenses.reduce(
                    (sum, exp) => sum + parseFloat(exp.amount),
                    0,
                  )
                  : 0;
                const balance = parseFloat(deposit.petty_cash) - totalExpense;
                return (
                  <React.Fragment key={deposit.deposit_id}>
                    <tr>
                      <td>{deposit.deposit_id}</td>
                      <td>{deposit.driver_name}</td>
                      <td>
                        {deposit.deposit_date
                          ? new Date(deposit.deposit_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>
                        <span className="text-success">
                          {parseFloat(deposit.petty_cash) === 0
                            ? parseFloat(deposit.petty_cash).toFixed(2)
                            : `${sym} ${parseFloat(deposit.petty_cash).toFixed(2)}`}

                        </span>
                      </td>
                      <td>
                        <span className="text-danger">
                          {totalExpense === 0
                            ? totalExpense.toFixed(2)
                            : `${sym} ${totalExpense.toFixed(2)}`}

                        </span>
                      </td>
                      <td>
                        <span className="text-primary fw-bold">
                          {balance === 0
                            ? balance.toFixed(2)
                            : `${sym} ${balance.toFixed(2)}`}

                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {deposit.receipt_image ? (
                          <button
                            className="btn btn-outline-primary btn-sm"
                            style={{
                              width: "60px",
                              height: "30px",
                              fontSize: "12px",
                              border: "2px solid rgb(0, 195, 133)",
                            }}
                            onClick={() => setSelectedDeposit(deposit)}
                            type="button"
                            aria-label="View deposit details"
                          >
                            View
                          </button>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-success btn-sm"
                          style={{
                            width: "70px",
                            height: "30px",
                            fontSize: "12px",
                            border: "2px solid #198754",
                          }}
                          onClick={() =>
                            navigate(
                              `/accounts/add-driver-expense?driver_id=${deposit.driver_id}&deposit_id=${deposit.deposit_id}`,
                            )
                          }
                        >
                          Expense
                        </button>
                      </td>

                      <td>
                        <ul className="d-flex gap-2 justify-content-center">
                          <li>
                            <button
                              className="btn btn-primary btn-sm"
                              style={{
                                width: "60px",
                                height: "30px",
                                fontSize: "12px",
                                border: "2px solid rgb(0, 195, 133)",
                              }}
                              onClick={() =>
                                navigate(
                                  `/accounts/edit-driver-deposit-cash/${deposit.deposit_id}`,
                                )
                              }
                              aria-label="Edit deposit"
                            >
                              Edit
                            </button>
                          </li>
                          <li>
                            <button
                              className="btn btn-danger btn-sm"
                              style={{
                                width: "60px",
                                height: "30px",
                                fontSize: "12px",
                                border: "2px solid #dc3545",
                              }}
                              onClick={() =>
                                handleDeleteClick(deposit.deposit_id)
                              }
                              aria-label="Delete deposit"
                            >
                              Delete
                            </button>
                          </li>
                          <li
                            className="dropdown"
                            style={{ position: "relative" }}
                          >
                            <button
                              className="btn btn-secondary btn-sm dropdown-toggle"
                              type="button"
                              onClick={() =>
                                setOpenDetailsId(
                                  openDetailsId === deposit.deposit_id
                                    ? null
                                    : deposit.deposit_id,
                                )
                              }
                              aria-expanded={
                                openDetailsId === deposit.deposit_id
                              }
                              style={{
                                width: "40px",
                                height: "30px",
                                fontSize: "16px",
                                border: "2px solid #6c757d",
                              }}
                              aria-label="Show transaction details"
                            >
                              ⋮
                            </button>
                          </li>
                        </ul>
                      </td>
                    </tr>
                    {openDetailsId === deposit.deposit_id && (
                      <tr>
                        <td
                          colSpan={8}
                          style={{ background: "#f8f9fa", padding: 0 }}
                        >
                          <TransactionDetails
                            name={deposit.driver_name}
                            balance={parseFloat(deposit.petty_cash)}
                            depositId={deposit.deposit_id}
                            onClose={() => setOpenDetailsId(null)}
                            currencySymbol={sym}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="8" className="text-center">
                  No deposits found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDeleteModal && (
        <>
          <style>{`
            .modal-footer .btn-cancel {
              background: #6c75a9;
              color: #fff;
              border: none;
              transition: background 0.2s;
            }
            .modal-footer .btn-cancel:hover, .modal-footer .btn-cancel:focus {
              background: #b0b3bb !important;
              color: #333 !important;
            }
            .modal-footer .btn-delete {
              background: #e74c3c;
              color: #fff;
              border: none;
              transition: background 0.2s;
            }
            .modal-footer .btn-delete:hover, .modal-footer .btn-delete:focus {
              background: #ffb3b3 !important;
              color: #b71c1c !important;
            }
          `}</style>
          <div
            className="modal fade show"
            style={{ display: "block", zIndex: 2000 }}
            tabIndex="-1"
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Deletion</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleteLoading}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="text-center">
                    <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
                    <h4>Are you sure?</h4>
                    <p>
                      Do you really want to delete this deposit? <br /> This
                      action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="modal-footer d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-delete"
                    onClick={handleDeleteConfirm}
                    disabled={deleteLoading}
                    style={{ minWidth: 90 }}
                  >
                    {deleteLoading ? (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                    ) : null}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050 }}
          ></div>
        </>
      )}

      {selectedDeposit && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block", zIndex: 2100 }}
            tabIndex="-1"
            aria-modal="true"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Deposit Details</h5>
                  <style>{`
  .view-close-btn {
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #ffffff;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
  }

  .view-close-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }

  .view-close-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .view-close-btn svg {
    font-size: 20px;
  }
`}</style>
                  <button
                    type="button"
                    className="view-close-btn"
                    onClick={() => setSelectedDeposit(null)}
                    aria-label="Close"
                  ><CloseIcon /></button>
                </div>
                <div className="modal-body">
                  {selectedDeposit.receipt_image && (
                    <div className="text-center mb-4">
                      <h6 className="mb-3">Receipt Image</h6>
                      <a
                        href={selectedDeposit.receipt_image}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <SafeImage
                          src={selectedDeposit.receipt_image}
                          alt="Receipt"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "400px",
                            borderRadius: "12px",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                            border: "2px solid #e9ecef",
                          }}
                        />
                      </a>
                      <div className="mt-2">
                        <small className="text-muted">
                          Click image to view full size
                        </small>
                      </div>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-primary mb-3">Basic Information</h6>
                        <div className="mb-2">
                          <strong>ID:</strong> {selectedDeposit.deposit_id}
                        </div>
                        <div className="mb-2">
                          <strong>Driver Name:</strong>{" "}
                          {selectedDeposit.driver_name}
                        </div>
                        <div className="mb-2">
                          <strong>Date:</strong>{" "}
                          {selectedDeposit.deposit_date
                            ? new Date(
                              selectedDeposit.deposit_date,
                            ).toLocaleDateString()
                            : "-"}
                        </div>
                        <div className="mb-2">
                          <strong>Payment Method:</strong>{" "}
                          {selectedDeposit.payment_method}
                        </div>
                        <div className="mb-2">
                          <strong>Receipt Number:</strong>{" "}
                          {selectedDeposit.receipt_number || "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="border rounded p-3 h-100">
                        <h6 className="text-success mb-3">Financial Details</h6>
                        <div className="mb-2">
                          <strong>Petty Cash:</strong>{" "}
                          <span className="text-success">
                            {parseFloat(selectedDeposit.petty_cash) === 0
                              ? parseFloat(selectedDeposit.petty_cash).toFixed(2)
                              : `${selectedDepositSym} ${parseFloat(selectedDeposit.petty_cash).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {selectedDeposit.notes && (
                      <div className="col-12">
                        <div className="border rounded p-3">
                          <h6 className="text-info mb-3">Notes</h6>
                          <div style={{ whiteSpace: "pre-line" }}>
                            {selectedDeposit.notes}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedDeposit(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 2050 }}
          ></div>
        </>
      )}
    </>
  );
};

export default DriverDepositCash;
